import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
	CircularProgress,
	Box,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Checkbox,
	FormControlLabel,
	Divider,
	Button,
	Alert,
	useTheme,
	Skeleton,
	TextField,
	MenuItem,
	Tooltip,
	Fab,
	Badge,
	Pagination,         // ── CHANGE 1: Added Pagination to MUI imports ──
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ProductCard from "../../components/ProductCard/ProductCard";
import { useSite } from "../../context/siteContext";
import PageTitle from "../../components/PageTitle/PageTitle";
import "./Products.css";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import PageNotFound from "../PageNotFound/PageNotFound";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import DescriptionIcon from "@mui/icons-material/Description";
import {
	ASSETS_BASE_URL,
	SERVER_BASE_URL,
	RSPL_LIST,
	DOCUMENT_EXTERNAL_ENABLED,
	DOCUMENT_EXTERNAL_URL,
	EXTERNAL_2D_ENABLED,
} from "../../config";
import { useOptions } from "../../context/optionContext";
import { useCart } from "../../context/cartContext";
import Swal from "sweetalert2";
import { useAuth } from "../../context/authContext";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import {
	ALERT_MESSAGES,
	BUTTON,
	LABELS,
	PLACEHOLDER,
	UI_TEXT,
	ROUTES,
	MODELSOURCE,
} from "../../constants";
import { useTranslation } from "react-i18next";
import MultipleModelViewer from "../../components/MultipleModelViewer/MultipleModelViewer";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import { getMachinesByIds } from "../../services/breadCrumbsService";
import { processModelFile, verifyResourceExists } from "../../services/modelService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import { getSavedForLaterBySiteId } from "../../services/savedForLetterService";
import { getTopPurchasedProduct } from "../../services/productsService";
import { getMachinesWithModels } from "../../services/myMachineService";
import { process2DModelFile } from "../../services/modelService";
import BomViewer from "../../components/BomViewer/BomViewer";
import BomProductQuickView from "../../components/BomProductQuickView/BomProductQuickView";
import { processModel } from "../../services/multipleModelViewer";
import { getAssignedMachineIdsForSite } from "../../services/documentService";
import {
	buildExternalLoginUrl,
	buildExternalTargetUrl,
	getMachineDocEntry,
	openExternalDocumentation,
} from "../../utilities/externalDocs";
import {
	indexBomTree,
	collectAncestorIds,
	MACHINE_NODE_PREFIX,
} from "../../utilities/bomUtils";
import {
	buildRsplCodeToLabelMap,
	getRsplLabelForCode,
	parseRsplList,
} from "../../utilities/rspl";
import { formatMachineNumberWithLine } from "../../utilities/machineDisplay";
import { trackModelOpened } from "../../utilities/analyticsService";
import { getBomLevelSortParts } from "../../utilities/globalServiceUtils";

const PRODUCT_FILTER_SORT_KEYS = {
	NAME_ASC: "name_asc",
	NAME_DESC: "name_desc",
	NUMBER_ASC: "number_asc",
	NUMBER_DESC: "number_desc",
	PRICE_ASC: "price_asc",
	PRICE_DESC: "price_desc",
};

const parseJsonArray = (value, fallback = []) => {
	if (!value) return fallback;
	try {
		const parsed = typeof value === "string" ? JSON.parse(value) : value;
		return Array.isArray(parsed) ? parsed : fallback;
	} catch {
		return fallback;
	}
};

const areSelectionArraysEqual = (currentValue, nextValue) => {
	if (currentValue === null && nextValue === null) return true;
	if (!Array.isArray(currentValue) || !Array.isArray(nextValue)) return false;
	if (currentValue.length !== nextValue.length) return false;
	const nextSet = new Set(nextValue);
	return currentValue.every((value) => nextSet.has(value));
};

const extractFilename = (path) => {
	if (!path) return "";
	const parts = String(path).split("/");
	const lastPart = parts[parts.length - 1] || "";
	return lastPart.replace(/\.[^/.]+$/, "");
};

const build2dUrl = (pathValue) => {
	if (!pathValue) return "";
	let normalized = String(pathValue).trim();
	if (!normalized) return "";
	if (/^https?:\/\//i.test(normalized)) return normalized;
	normalized = normalized.replace(/\\/g, "/");
	normalized = normalized.replace(/^https?:\/\/[^/]+/i, "");
	normalized = normalized.replace(/^\/?stock-models\//i, "");
	normalized = normalized.replace(/^\/?models\//i, "");
	normalized = normalized.replace(/^\/?modelfiles\//i, "");
	normalized = normalized.replace(/^\/+/i, "");
	if (!/^2d\//i.test(normalized)) {
		normalized = `2D/${normalized}`;
	}
	if (!/\.pdf$/i.test(normalized)) {
		normalized = `${normalized}.pdf`;
	}
	return `${SERVER_BASE_URL}/stock-models/${normalized}`;
};

const normalize3DFile = (file) => {
	if (!file) return { glb: null, html: null };
	if (typeof file === "string") return { glb: null, html: file };
	return {
		glb: file.glb || null,
		html: file.html || null,
	};
};

const getModelCapabilities = (modelData = {}) => {
	const main3d = normalize3DFile(modelData?.["3dfile"]);
	const bomModels = Array.isArray(modelData?.boms)
		? modelData.boms.filter((item) => {
			const threeD = normalize3DFile(item?.["3dfile"]);
			return Boolean(threeD.glb || threeD.html || item?.["2dfile"]);
		})
		: [];
	const hasBOM2D = bomModels.some((item) => item?.["2dfile"]);
	const hasBOM3D = bomModels.some((item) => {
		const threeD = normalize3DFile(item?.["3dfile"]);
		return Boolean(threeD.glb || threeD.html);
	});
	const has2D = Boolean(modelData?.["2dfile"] || hasBOM2D);
	const has3D = Boolean(
		main3d.glb ||
		main3d.html ||
		(Array.isArray(modelData?.splitfiles) && modelData.splitfiles.length > 0) ||
		hasBOM3D
	);
	const multiple =
		(has2D && has3D) ||
		bomModels.length > 0 ||
		(Array.isArray(modelData?.splitfiles) && modelData.splitfiles.length > 0);

	return { has2D, has3D, multiple };
};

const hasRenderableModelData = (modelData = {}) => {
	const { has2D, has3D, multiple } = getModelCapabilities(modelData);
	return has2D || has3D || multiple;
};

const normalizeSpareLevelValue = (value) => {
	if (value === undefined || value === null) return "";
	const normalized = String(value).trim();
	return normalized || "";
};

const getSpareLevelFromRecord = (record) =>
	normalizeSpareLevelValue(
		record?.spares_level ??
		record?.sparesLevel ??
		record?.spare_level ??
		record?.spareLevel
	);

const buildSpareLevelLookup = (products = [], bom = [], bomTree = []) => {
	const byProductId = new Map();
	const byProductNumber = new Map();

	const register = (identifier, spareLevel, targetMap) => {
		const normalizedSpareLevel = normalizeSpareLevelValue(spareLevel);
		if (!identifier || !normalizedSpareLevel) return;
		const key = String(identifier).trim();
		if (!key || targetMap.has(key)) return;
		targetMap.set(key, normalizedSpareLevel);
	};

	const registerRecord = (record) => {
		const spareLevel = getSpareLevelFromRecord(record);
		if (!spareLevel) return;
		register(record?.product_id ?? record?.productId, spareLevel, byProductId);
		register(
			record?.product_number ??
			record?.productNumber ??
			record?.part_number ??
			record?.partNumber,
			spareLevel,
			byProductNumber
		);
	};

	const walkBomTree = (nodes = []) => {
		nodes.forEach((node) => {
			if (!node) return;
			const spareLevel = getSpareLevelFromRecord(node);
			if (spareLevel) {
				register(node?.productId ?? node?.product_id, spareLevel, byProductId);
				register(node?.productNumber ?? node?.product_number, spareLevel, byProductNumber);
			}
			if (Array.isArray(node.children) && node.children.length > 0) {
				walkBomTree(node.children);
			}
		});
	};

	products.forEach(registerRecord);
	bom.forEach(registerRecord);
	walkBomTree(bomTree);

	return { byProductId, byProductNumber };
};

const BOM_EXPORT_COLUMNS = [

	"machine_number",
	"part_number",
	"parent_part_number",
	"bom_level",
	"quantity",
	"uom",
	"spares_level",
	"part_name",
	"machine_revision",
	// "parent_part_name",
];

const BOM_EXPORT_OEM_COLUMNS = ["manufacturer_name", "manufacturer_item_number"];

const sanitizeFileNameSegment = (value, fallback = "BOM") => {
	const normalized = String(value || fallback)
		.trim()
		.replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")
		.replace(/\s+/g, "_");
	return normalized || fallback;
};

const resolveManufacturingName = (product = {}) =>
	product?.manufacturing_name ??
	product?.part_company ??
	product?.manufacturer_name ??
	"";

const resolveManufacturingItemNumber = (product = {}) =>
	product?.manufacturing_item_number ??
	product?.part_number ??
	product?.manufacturer_item_number ??
	"";



const compareBomLevels = (levela, levelb) => {
	const alevelParts = getBomLevelSortParts(levela);
	const blevelParts = getBomLevelSortParts(levelb);

	if (alevelParts.length === 0 && blevelParts.length === 0) return 0;
	if (alevelParts.length === 0) return 1;
	if (blevelParts.length === 0) return -1;

	const maxLength = Math.max(alevelParts.length, blevelParts.length);
	for (let index = 0; index < maxLength; index += 1) {
		if (index >= alevelParts.length) return -1;
		if (index >= blevelParts.length) return 1;

		const aPart = alevelParts[index];
		const bPart = blevelParts[index];
		if (aPart === bPart) continue;

		if (typeof aPart === "number" && typeof bPart === "number") {
			return aPart - bPart;
		}

		return String(aPart).localeCompare(String(bPart), undefined, {
			numeric: true,
			sensitivity: "base",
		});
	}

	return 0;
};

const buildBomExportRows = (bomRows = [], products = [], includeOemColumns = false) => {
	const productLookup = new Map();
	(products || []).forEach((product) => {
		const key = String(product?.product_id ?? "").trim();
		if (key) {
			productLookup.set(key, product);
		}
	});

	const sortedBomRows = (bomRows || [])
		.map((row, index) => ({ row, index }))
		.sort((a, b) => {
			const levelCompare = compareBomLevels(
				a.row?.del_bom_level ?? a.row?.bom_level,
				b.row?.del_bom_level ?? b.row?.bom_level
			);
			return levelCompare || a.index - b.index;
		})
		.map(({ row }) => row);

	return sortedBomRows.map((row, index) => {
		const productKey = String(row?.product_id ?? "").trim();
		const product = productLookup.get(productKey) || {};
		return {
			// id: row?.id ?? index + 1,
			// machine_id: row?.machine_id ?? "",
			machine_number: row?.machine_number ?? "",
			// product_id: row?.product_id ?? "",
			part_number: row?.product_number ?? "",
			parent_part_number: row?.parent_product_number ?? "",
			bom_level: row?.del_bom_level ?? "",
			quantity: row?.quantity ?? "",
			uom: row?.uom ?? "",
			spares_level: row?.spares_level ?? "",
			part_name: row?.product_name ?? "",
			// parent_product_id: row?.parent_product_id ?? "",
			machine_revision: row?.machine_revision ?? "",
			// parent_product_name: row?.parent_product_name ?? "",
			// resolved_parent_product_number: row?.resolved_parent_product_number ?? "",
			...(includeOemColumns
				? {
					manufacturer_name: resolveManufacturingName(product),
					manufacturer_item_number: resolveManufacturingItemNumber(product),
				}
				: {}),
		};
	});
};

const SPARE_LEVEL_TOOLTIPS = {
	"1": "Essential consumables for routine maintenance.",
	"2": "Mechanical and electrical wear components.",
	"3": "Precision components for performance and accuracy.",
	"4": "Core motion and drive system components.",
	"5": "Critical control and automation components",
};

const normalizeProductId = (value) => {
	if (value === undefined || value === null) return "";
	const normalized = String(value).trim();
	return normalized || "";
};

const toNumberOrOne = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getBomNodeQuantity = (node = {}) =>
	toNumberOrOne(
		node?.bomQuantity ??
		node?.quantity ??
		node?.qty ??
		node?.bom_quantity ??
		node?.bom_qty
	);

const getBomNodeLevel = (node = {}, fallbackLevel = 0) => {
	const rawLevel = node?.bomLevel ?? node?.bom_level ?? node?.level;
	const parsedLevel = Number(rawLevel);
	return Number.isFinite(parsedLevel) ? parsedLevel : fallbackLevel;
};

const Products = () => {
	const { machineID } = useParams();
	const [searchParams] = useSearchParams();
	const selectedSiteLineId = searchParams.get("site_line_id") || "";
	const selectedLineName = searchParams.get("line_name") || "";
	const selectedLineNumber = searchParams.get("line_number") || "";
	const forceBomTreeView = searchParams.get("view") === MODELSOURCE.BOM_TREE;
	const [products, setProducts] = useState([]);
	const [topProducts, setTopProducts] = useState([]);
	const topFetchTimer = useRef(null);
	const [loading, setLoading] = useState(true);
	const [machine_name, setmachine_name] = useState("");
	const [machine_number, setmachine_number] = useState("");
	const [line_name, setLineName] = useState("");
	const [line_number, setLineNumber] = useState("");
	const [machineSerialForDocs, setMachineSerialForDocs] = useState("");
	const [error, setError] = useState(null);
	const [pageSize, setPageSize] = useState(50);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [productSearchTerm, setProductSearchTerm] = useState("");
	const [selectedFilters, setSelectedFilters] = useState(null);
	const [selectedSpareLevels, setSelectedSpareLevels] = useState(null);
	const { selectedSite, selectedSiteCurrency, currencySymbol, selectedSiteData, loading: siteContextLoading } = useSite();
	const [sortOption, setSortOption] = useState("Price Desc");
	const [has3DModel, setHas3DModel] = useState(false);
	const [has2DModel, setHas2DModel] = useState(false);
	const [m3DButtonLoading, setM3DButtonLoading] = useState(false);
	const [hasMultipleModels, setHasMultipleModels] = useState(false);
	const [hasAssignedDocumentation, setHasAssignedDocumentation] = useState(false);
	const [modelData, setModelData] = useState({});
	const [bomTreeData, setBomTreeData] = useState([]);
	const [bomRows, setBomRows] = useState([]);
	const [bomLoading, setBomLoading] = useState(true);
	const [checkedBomProductIds, setCheckedBomProductIds] = useState([]);
	const [checkedBomNodeIds, setCheckedBomNodeIds] = useState([]);
	const [selectedBomNodeId, setSelectedBomNodeId] = useState(null);
	const [expandedNodes, setExpandedNodes] = useState([]);
	const treeItemRefs = useRef({});
	const [modelsModalOpen, setModelsModalOpen] = useState(false);
	const [topProductsLoading, setTopProductsLoading] = useState(true);
	const [bomModalOpen, setBomModalOpen] = useState(false);
	const [bomProductQuickView, setBomProductQuickView] = useState({
		open: false,
		productId: null,
	});
	const [bomModalOpening, setBomModalOpening] = useState(false);
	const [bomSelectionReady, setBomSelectionReady] = useState(false);
	const syncSourceRef = useRef(null);
	const activeFilterGroupRef = useRef(null);
	// ── CHANGE 2: REMOVED loadMoreRef and loadMorePendingRef (infinite scroll refs) ──
	const navigate = useNavigate();
	const { user } = useAuth();
	const theme = useTheme();
	const primaryColor = theme.palette.primary.main;
	const { addMultipleToCart, cartItems, updateCart } = useCart();
	const { t } = useTranslation();
	const { optionKeys } = useOptions();
	const lineQuery = (() => {
		if (!selectedSiteLineId) return "";
		const p = new URLSearchParams();
		p.set("site_line_id", selectedSiteLineId);
		if (selectedLineName) p.set("line_name", selectedLineName);
		if (selectedLineNumber || line_number) p.set("line_number", selectedLineNumber || line_number);
		return `?${p.toString()}`;
	})();
	const locationIdForDocs =
		selectedSiteData?.customer_site_number ||
		selectedSiteData?.customer_site_name ||
		selectedSite ||
		"";
	const machineDocEntry = useMemo(
		() =>
			getMachineDocEntry({
				locationId: locationIdForDocs,
				serialNumber: machineSerialForDocs,
			}),
		[locationIdForDocs, machineSerialForDocs]
	);
	const shouldShowBomDocIcon = DOCUMENT_EXTERNAL_ENABLED
		? machineDocEntry?.HasDoc === "Y"
		: hasAssignedDocumentation;

	useEffect(() => {
		let cancelled = false;
		const currentMachineId = Number(machineID);
		const currentSiteId = Number(selectedSiteData?.site_id);

		if (
			DOCUMENT_EXTERNAL_ENABLED ||
			!Number.isFinite(currentMachineId) ||
			currentMachineId <= 0 ||
			!Number.isFinite(currentSiteId) ||
			currentSiteId <= 0
		) {
			setHasAssignedDocumentation(false);
			return () => {
				cancelled = true;
			};
		}

		getAssignedMachineIdsForSite(currentSiteId)
			.then((assignedMachineIds) => {
				if (cancelled) return;
				setHasAssignedDocumentation(assignedMachineIds.has(currentMachineId));
			})
			.catch((error) => {
				console.warn("Failed to resolve machine documentation availability:", error);
				if (cancelled) return;
				setHasAssignedDocumentation(false);
			});

		return () => {
			cancelled = true;
		};
	}, [machineID, selectedSiteData?.site_id]);

	const handleDocumentationNavigate = () => {
		if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL || !machineID) {
			navigate(machineID ? `/documents/${machineID}` : ROUTES.DOCUMENT);
			return;
		}
		if (!machineDocEntry || machineDocEntry.HasDoc !== "Y") return;
		const username = selectedSiteData?.amf_method_username_ln;
		const password = selectedSiteData?.amf_method_password_ln;
		const loginUrl = buildExternalLoginUrl({
			templateUrl: DOCUMENT_EXTERNAL_URL,
			username,
			password,
		});
		const targetUrl = buildExternalTargetUrl({
			username,
			group: machineDocEntry.Group,
			machine: machineSerialForDocs,
		});
		if (!loginUrl || !targetUrl) {
			navigate(machineID ? `/documents/${machineID}` : ROUTES.DOCUMENT);
			return;
		}
		const targetWindow = window.open("about:blank", "_blank");
		openExternalDocumentation({ loginUrl, targetUrl, targetWindow });
	};

	const normalizedMachineName = useMemo(
		() => (Array.isArray(machine_name) ? machine_name.join(", ") : machine_name || ""),
		[machine_name]
	);

	const normalizedMachineNumber = useMemo(
		() => (Array.isArray(machine_number) ? machine_number.join(", ") : machine_number || ""),
		[machine_number]
	);
	const normalizedMachineNumberDisplay = useMemo(
		() => formatMachineNumberWithLine(normalizedMachineNumber, ""),
		[normalizedMachineNumber]
	);

	const pageTitleText = useMemo(() => {
		if (normalizedMachineName || normalizedMachineNumberDisplay) {
			return [normalizedMachineName, normalizedMachineNumberDisplay].filter(Boolean).join(" | ");
		}
		return t("UI_TEXT.MACHINE");
	}, [normalizedMachineName, normalizedMachineNumberDisplay, t]);

	const machineNodeId = useMemo(
		() => `${MACHINE_NODE_PREFIX}-${machineID || "active"}`,
		[machineID]
	);

	const { flatNodes, nodeMap, machineProductIds } = useMemo(
		() => indexBomTree(bomTreeData),
		[bomTreeData]
	);

	const machineNodeIds = useMemo(
		() => flatNodes.filter((node) => node?.productId).map((node) => node.nodeId),
		[flatNodes]
	);

	const selectedBomProductIds = useMemo(() => {
		const selectedNodeSet = new Set(checkedBomNodeIds || []);
		const seen = new Set();
		const ids = [];
		flatNodes.forEach((node) => {
			if (!node?.nodeId || !selectedNodeSet.has(node.nodeId) || !node.productId) return;
			if (seen.has(node.productId)) return;
			seen.add(node.productId);
			ids.push(node.productId);
		});
		return ids;
	}, [checkedBomNodeIds, flatNodes]);

	const checkedProductIdSet = useMemo(
		() => new Set(selectedBomProductIds || []),
		[selectedBomProductIds]
	);
console.log(selectedSiteData,'selected site data');

	const showOemShareFields = String(selectedSiteData?.oem_share_approved ?? "0").trim() === "1";

	const handleExportBom = useCallback(() => {
		if (!Array.isArray(bomRows) || bomRows.length === 0) {
			Swal.fire({
				icon: "info",
				title: "Export BOM",
				text: "No BOM data is available to export.",
			});
			return;
		}

		const exportRows = buildBomExportRows(bomRows, products, showOemShareFields);
		const exportColumns = showOemShareFields
			? [...BOM_EXPORT_COLUMNS, ...BOM_EXPORT_OEM_COLUMNS]
			: BOM_EXPORT_COLUMNS;
		const worksheet = XLSX.utils.json_to_sheet(exportRows, {
			header: exportColumns,
		});
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "BOM");

		const fileNameBase = sanitizeFileNameSegment(
			normalizedMachineNumber || normalizedMachineName || machineID || "BOM"
		);
		XLSX.writeFile(workbook, `${fileNameBase}_BOM_Export.xlsx`);
	}, [bomRows, machineID, normalizedMachineName, normalizedMachineNumber, products, showOemShareFields]);

	const resolvedRsplList = useMemo(
		() => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
		[optionKeys?.rsplList]
	);
	const productsDefaultViewMode = String(optionKeys?.productsDefaultViewMode || "cards").trim().toLowerCase();
	const showBomTreeOnMainCanvas = forceBomTreeView || productsDefaultViewMode === MODELSOURCE.BOM_TREE;
	const showBomExportButton = optionKeys?.enableBomExport !== "false";
	const rsplMapping = useMemo(
		() => buildRsplCodeToLabelMap(resolvedRsplList),
		[resolvedRsplList]
	);

	const categoryArray = useMemo(() => {
		const unique = Array.from(new Set((resolvedRsplList || []).map((item) => item.value)));
		unique.push(t('UI_TEXT.OTHER'));
		return unique;
	}, [resolvedRsplList, t]);

	const availableCategoryArray = useMemo(() => {
		if (!products.length) return [];
		const productTypeSet = new Set(
			products
				.map((product) => product.ProductType)
				.filter(Boolean)
		);
		const orderedKnownTypes = categoryArray.filter((type) => productTypeSet.has(type));
		const unknownTypes = Array.from(productTypeSet).filter(
			(type) => !categoryArray.includes(type)
		);
		return [...orderedKnownTypes, ...unknownTypes];
	}, [products, categoryArray]);

	const productTypeByProductId = useMemo(() => {
		const map = new Map();
		products.forEach((product) => {
			if (product?.product_id) {
				map.set(product.product_id, product.ProductType);
			}
		});
		return map;
	}, [products]);

	const productIdsByCategory = useMemo(() => {
		const map = new Map();
		flatNodes.forEach((node) => {
			const productId = node?.productId;
			const nodeId = node?.nodeId;
			if (!productId || !nodeId) return;
			const category = productTypeByProductId.get(productId);
			if (!category) return;
			if (!map.has(category)) {
				map.set(category, []);
			}
			map.get(category).push(nodeId);
		});
		return map;
	}, [flatNodes, productTypeByProductId]);

	const availableSpareLevelArray = useMemo(() => {
		if (!products.length) return [];
		const spareLevelSet = new Set(
			products
				.map((product) => product.SpareLevel)
				.filter(Boolean)
		);
		return ["1", "2", "3", "4", "5"].filter((level) => spareLevelSet.has(level));
	}, [products]);

	const spareLevelByProductId = useMemo(() => {
		const map = new Map();
		products.forEach((product) => {
			if (product?.product_id) {
				map.set(product.product_id, product.SpareLevel);
			}
		});
		return map;
	}, [products]);

	const bomModelAvailability = useMemo(() => {
		const map = new Map();
		const registerEntry = (value, type, availability) => {
			if (value === undefined || value === null) return;
			const normalized = String(value).trim();
			if (!normalized) return;
			const key = `${type}:${normalized.toUpperCase()}`;
			if (!map.has(key)) {
				map.set(key, availability);
			}
		};
		const bomModels = modelData?.boms || [];
		bomModels.forEach((item) => {
			const threeD = normalize3DFile(item["3dfile"]);
			const has3D = Boolean(threeD.glb || threeD.html);
			const has2D = Boolean(item["2dfile"]);
			if (!has3D && !has2D) return;
			const productNumber =
				item?.product_number || item?.productNumber || item?.filename || "";
			const availability = {
				has3D,
				has2D,
				file3d: item["3dfile"] || "",
				file2d: item["2dfile"] || "",
				filename: productNumber,
				productNumbers: productNumber ? [productNumber] : [],
				raw: item,
			};
			registerEntry(productNumber, "pn", availability);
			registerEntry(item?.product_id, "id", availability);
			registerEntry(item?.productId, "id", availability);
		});
		return map;
	}, [modelData]);

	const hasBomData = machineProductIds.length > 0;

	const normalizedSearchTerm = productSearchTerm.trim().toLowerCase();

	const searchMatchIds = useMemo(() => {
		if (!normalizedSearchTerm) return [];
		return flatNodes
			.filter((node) => {
				const name = node.productName?.toLowerCase() || "";
				const number = node.productNumber?.toLowerCase() || "";
				return (
					name.includes(normalizedSearchTerm) ||
					number.includes(normalizedSearchTerm)
				);
			})
			.map((node) => node.nodeId);
	}, [flatNodes, normalizedSearchTerm]);

	const bulkAddCategories = useMemo(() => {
		const counts = products.reduce((acc, p) => {
			const cat = p.ProductType;
			if (!cat) return acc;
			acc[cat] = (acc[cat] || 0) + 1;
			acc[`${cat}__purch`] = (acc[`${cat}__purch`] || 0) + 1;
			return acc;
		}, {});

		return categoryArray
			.filter((cat) => cat !== t('UI_TEXT.OTHER'))
			.map((cat) => ({
				category: cat,
				totalCount: counts[cat] || 0,
				purchasableCount: counts[`${cat}__purch`] || 0,
			}))
			.filter(({ totalCount }) => totalCount > 0);
	}, [categoryArray, products]);

	const toI18nKey = (cat) =>
		`BUTTON.ADD_ALL_${String(cat).toUpperCase().replace(/[^A-Z0-9]+/g, "_")}_PARTS`;

	const getBulkButtonLabel = useCallback(
		(cat, purchasableCount) => {
			return `Add all ${cat} parts (${purchasableCount})`;
		},
		[t]
	);

	const sortOptions = useMemo(
		() => [
			t('GENERAL.NAME_ASC'),
			t('GENERAL.NAME_DESC'),
			t('GENERAL.NUMBER_ASC'),
			t('GENERAL.NUMBER_DESC'),
			t('GENERAL.PRICE_ASC'),
			t('GENERAL.PRICE_DESC'),
		],
		[t]
	);
	const getSortLabelFromKey = useCallback(
		(sortKey) => {
			switch (String(sortKey || "").trim().toLowerCase()) {
				case PRODUCT_FILTER_SORT_KEYS.NAME_ASC:
					return t('GENERAL.NAME_ASC');
				case PRODUCT_FILTER_SORT_KEYS.NAME_DESC:
					return t('GENERAL.NAME_DESC');
				case PRODUCT_FILTER_SORT_KEYS.NUMBER_ASC:
					return t('GENERAL.NUMBER_ASC');
				case PRODUCT_FILTER_SORT_KEYS.NUMBER_DESC:
					return t('GENERAL.NUMBER_DESC');
				case PRODUCT_FILTER_SORT_KEYS.PRICE_ASC:
					return t('GENERAL.PRICE_ASC');
				case PRODUCT_FILTER_SORT_KEYS.PRICE_DESC:
				default:
					return t('GENERAL.PRICE_DESC');
			}
		},
		[t]
	);
	const pageSizeOptions = [25, 50, 100];

	const handleProductSearchChange = (value) => {
		setCurrentPage(1);
		setProductSearchTerm(value);
	};

	const handlePageSizeChange = (event) => {
		setCurrentPage(1);
		setPageSize(Number(event.target.value));
	};

	const handleFilterChange = (event) => {
		const { value, checked } = event.target;
		syncSourceRef.current = "filters";
		activeFilterGroupRef.current = "partType";
		setSelectedFilters((prevFilters) => {
			const currentFilters = prevFilters === null ? availableCategoryArray : prevFilters;
			const nextFilters = checked
				? Array.from(new Set([...currentFilters, value]))
				: currentFilters.filter((filter) => filter !== value);
			return nextFilters.length === availableCategoryArray.length ? null : nextFilters;
		});
	};

	const handleAllChange = (event) => {
		syncSourceRef.current = "filters";
		activeFilterGroupRef.current = "partType";
		if (event.target.checked) {
			setSelectedFilters(null);
		} else {
			setSelectedFilters([]);
		}
	};

	const handleSpareLevelFilterChange = (event) => {
		const { value, checked } = event.target;
		syncSourceRef.current = "filters";
		activeFilterGroupRef.current = "spareLevel";
		setSelectedSpareLevels((prevLevels) => {
			const currentLevels = prevLevels === null ? availableSpareLevelArray : prevLevels;
			const nextLevels = checked
				? Array.from(new Set([...currentLevels, value]))
				: currentLevels.filter((level) => level !== value);
			return nextLevels.length === availableSpareLevelArray.length ? null : nextLevels;
		});
	};

	const handleAllSpareLevelsChange = (event) => {
		syncSourceRef.current = "filters";
		activeFilterGroupRef.current = "spareLevel";
		if (event.target.checked) {
			setSelectedSpareLevels(null);
		} else {
			setSelectedSpareLevels([]);
		}
	};

	const handleTreeSelection = (nodeId) => {
		if (nodeId === machineNodeId || !nodeMap.has(nodeId)) {
			setSelectedBomNodeId(null);
			return;
		}
		setSelectedBomNodeId(nodeId);
	};

	const handleBomProductClick = (node) => {
		if (!node?.productId) return;
		setBomProductQuickView({
			open: true,
			productId: node.productId,
		});
	};

	const showBomModelError = useCallback(() => {
		Swal.fire({
			icon: "error",
			title: t("ALERT_MESSAGES.ERROR"),
			text: t("ALERT_MESSAGES.MODEL_OPEN_FAILED"),
		});
	}, [t]);

	const openBomModelUrl = useCallback(async (url) => {
		if (!url) return false;
		const exists = await verifyResourceExists(url);
		if (!exists) {
			return false;
		}
		window.open(url);
		return true;
	}, []);

	const handleBomModelIconClick = useCallback(
		async ({ node, modelInfo, type }) => {
			if (!modelInfo || !node) return;
			try {
				if (type === "2d" && modelInfo.file2d) {
					if (EXTERNAL_2D_ENABLED) {
						const pdfUrl = build2dUrl(modelInfo.file2d);
						const opened = await openBomModelUrl(pdfUrl);
						if (!opened) {
							showBomModelError();
						} else {
							trackModelOpened("2D", {
								machine_id: machineID,
								machine_name,
								machine_number,
								model_file: extractFilename(modelInfo.file2d),
								model_format: "pdf",
								model_source: MODELSOURCE.BOM_TREE,
								product_id: node.productId || node.product_id || "",
								product_number: node.productNumber || node.product_number || "",
							});
						}
					} else {
						const filename = extractFilename(modelInfo.file2d);
						if (!filename) {
							showBomModelError();
							return;
						}
						trackModelOpened("2D", {
							machine_id: machineID,
							machine_name,
							machine_number,
							model_file: filename,
							model_format: "pdf",
							model_source: MODELSOURCE.BOM_TREE,
							product_id: node.productId || node.product_id || "",
							product_number: node.productNumber || node.product_number || "",
						});
						navigate(`/2d-model-viewer/${machineID}/${filename}`);
					}
					return;
				}
				const raw = modelInfo.raw || {};
				const threeD = raw["3dfile"];
				let glbPath = null;
				let htmlPath = null;
				if (threeD) {
					if (typeof threeD === "string") {
						htmlPath = threeD;
					} else {
						glbPath = threeD.glb || null;
						htmlPath = threeD.html || null;
					}
				}

				if (type === "2d") {
					showBomModelError();
					return;
				}

				if (glbPath) {
					const filename =
						extractFilename(glbPath) ||
						modelInfo.filename ||
						node.productNumber ||
						node.product_number ||
						raw.product_number;
					if (!filename) {
						showBomModelError();
						return;
					}
					trackModelOpened("3D", {
						machine_id: machineID,
						machine_name,
						machine_number,
						model_file: filename,
						model_format: "glb",
						model_source: MODELSOURCE.BOM_TREE,
						product_id: node.productId || node.product_id || "",
						product_number: node.productNumber || node.product_number || "",
					});
					navigate(`/model-viewer/${machineID}/${filename}`);
					return;
				}

				if (!htmlPath) {
					showBomModelError();
					return;
				}

				const filename =
					extractFilename(htmlPath) ||
					modelInfo.filename ||
					node.productNumber ||
					node.product_number ||
					modelInfo?.raw?.product_number;
				if (!filename) {
					showBomModelError();
					return;
				}
				const productNumbers =
					(modelInfo.productNumbers && modelInfo.productNumbers.length > 0
						? modelInfo.productNumbers
						: [node.productNumber || node.product_number || filename].filter(Boolean)) || [];
				const response = await processModel(
					"/models/process-modelfile",
					filename,
					machine_name,
					(selectedSiteCurrency || "USD").toUpperCase(),
					user?.user_id,
					productNumbers,
					machineID,
					primaryColor,
					selectedSite,
					machine_number
				);
				if (response.status === 200 && response.data?.url) {
					const fullUrl = `${SERVER_BASE_URL}${response.data.url}`;
					const opened = await openBomModelUrl(fullUrl);
					if (!opened) {
						showBomModelError();
					} else {
						trackModelOpened("3D", {
							machine_id: machineID,
							machine_name,
							machine_number,
							model_file: filename,
							model_format: "html",
							model_source: MODELSOURCE.BOM_TREE,
							product_id: node.productId || node.product_id || "",
							product_number: node.productNumber || node.product_number || "",
						});
					}
				} else {
					showBomModelError();
				}
			} catch (error) {
				console.error("Failed to open BOM model", error);
				showBomModelError();
			}
		},
		[
			machineID,
			machine_name,
			machine_number,
			navigate,
			openBomModelUrl,
			selectedSite,
			selectedSiteCurrency,
			showBomModelError,
			primaryColor,
			user?.user_id,
		]
	);

	const handleCloseBomProductQuickView = () => {
		setBomProductQuickView({ open: false, productId: null });
	};

	const handleBomSelectionChange = (nextNodeIds = []) => {
		//syncSourceRef.current = "bom";
		setCheckedBomNodeIds(nextNodeIds);
	};

	const handleClearTreeSelection = () => {
		setSelectedBomNodeId(null);
	};

	const handleNavigateToProductDetails = (productId) => {
		if (!productId || !machineID) return;
		setBomModalOpen(false);
		navigate(`${ROUTES.PRODUCT_DETAILS(machineID, productId)}${lineQuery}`);
	};

	const handleOpenBomModal = () => {
		setBomModalOpening(true);
		setBomModalOpen(true);
	};

	const selectedBomCount = useMemo(
		() =>
			(checkedBomNodeIds || [])
				.map((id) => normalizeProductId(id))
				.filter(Boolean).length,
		[checkedBomNodeIds]
	);

	// Builds the cart that "Add to Quote" will save: merges the selected BOM nodes into the
	// existing cart using the cart key (product + site + customer + machine + line + bom level +
	// assembly), so a part already in the quote under a different machine still gets its own line.
	const bomQuoteProjection = useMemo(() => {
		const selectedSet = new Set(
			(checkedBomNodeIds || []).map((id) => normalizeProductId(id)).filter(Boolean)
		);

		const desiredQtyByProductId = new Map();
		const bomRefsByProductId = new Map();
		const walk = (nodes = [], parentNode = null, depth = 0) => {
			nodes.forEach((node) => {
				const nodeProductId = normalizeProductId(node?.productId);
				const nodeId = normalizeProductId(node?.nodeId);
				if (nodeProductId && nodeId && selectedSet.has(nodeId)) {
					const qty = getBomNodeQuantity(node);
					desiredQtyByProductId.set(
						nodeProductId,
						(desiredQtyByProductId.get(nodeProductId) || 0) + 1
					);
					const refs = bomRefsByProductId.get(nodeProductId) || [];
					refs.push({
						bom_level: getBomNodeLevel(node, depth),
						bom_qty: qty,
						assembly_name:
							parentNode?.productName ||
							parentNode?.product_name ||
							node?.machineName ||
							machine_name ||
							"",
					});
					bomRefsByProductId.set(nodeProductId, refs);
				}
				if (Array.isArray(node?.children) && node.children.length > 0) {
					walk(node.children, node, depth + 1);
				}
			});
		};
		walk(bomTreeData, null, 0);

		const nextCart = [...(cartItems || [])];

		// Index existing lines by their scoped cart key. A findIndex() per selected product would
		// be O(products x cart size) and this recomputes on every checkbox click.
		const indexByCartKey = new Map();
		nextCart.forEach((item, index) => {
			if (
				String(item?.Site) !== String(selectedSite) ||
				String(item?.site_id ?? item?.siteId ?? "") !== String(selectedSiteData?.site_id ?? "") ||
				String(item?.cus_id ?? item?.customer_id ?? item?.customerId ?? "") !== String(selectedSiteData?.cus_id ?? "")
			) {
				return;
			}
			const key = String(item?.CartKey || "");
			if (key && !indexByCartKey.has(key)) indexByCartKey.set(key, index);
		});

		desiredQtyByProductId.forEach((qty, productId) => {
			const refs = bomRefsByProductId.get(productId) || [];
			const primaryRef = refs[0] || {};
			const cartKey = [
				String(productId || ""),
				String(selectedSite || ""),
				String(selectedSiteData?.site_id || ""),
				String(selectedSiteData?.cus_id || ""),
				String(machineID || ""),
				String(selectedSiteLineId || ""),
				String(primaryRef?.bom_level ?? ""),
				String(primaryRef?.assembly_name || ""),
			].join("|");
			const existingIndex = indexByCartKey.has(cartKey) ? indexByCartKey.get(cartKey) : -1;
			if (existingIndex > -1) {
				const existingQty = Number(nextCart[existingIndex]?.Quantity || 0) || 0;
				nextCart[existingIndex] = {
					...nextCart[existingIndex],
					Quantity: existingQty + qty,
					bom_refs: refs,
					bom_level: primaryRef?.bom_level ?? nextCart[existingIndex]?.bom_level ?? null,
				};
				return;
			}
			indexByCartKey.set(cartKey, nextCart.length);
			nextCart.push({
				ProductID: productId,
				Quantity: qty,
				Site: selectedSite,
				site_id: selectedSiteData?.site_id,
				cus_id: selectedSiteData?.cus_id,
				machine_id: machineID || null,
				site_line_id: selectedSiteLineId || null,
				line_name: line_name || selectedLineName || "",
				line_number: line_number || selectedLineNumber || "",
				bom_level: primaryRef?.bom_level ?? null,
				bom_refs: refs,
				CartKey: cartKey,
			});
		});

		return { nextCart };
	}, [
		checkedBomNodeIds,
		bomTreeData,
		cartItems,
		selectedSite,
		selectedSiteData?.site_id,
		selectedSiteData?.cus_id,
		machineID,
		selectedSiteLineId,
		machine_name,
		line_name,
		line_number,
		selectedLineName,
		selectedLineNumber,
	]);

	const handleAddSelectedToQuote = async () => {
		try {
			if (!selectedSite) return;
			const selectedSet = new Set(
				(checkedBomNodeIds || []).map((id) => normalizeProductId(id)).filter(Boolean)
			);
			if (selectedSet.size === 0) {
				Swal.fire({
					icon: "info",
					title: t("CART.NO_PARTS_FOUND"),
					text: t("UI_TEXT.SELECT_PARTS_IN_BOM_TREE"),
				});
				return;
			}

			// Use the shared projection so the tooltip, this dialog and the save can never disagree.
			const { nextCart } = bomQuoteProjection;

			await updateCart(nextCart);
			Swal.fire({
				icon: "success",
				title: t("CART.ADDED_TO_CART_1"),
				text: t("UI_TEXT.QUOTE_UPDATED_WITH_SELECTED_PARTS", {
					defaultValue: "Quote updated with selected machine parts.",
				}),
			});
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("GENERAL.FAILED"),
				text: t("CART.COULD_NOT_ADD_PARTS_TO_CART"),
			});
		}
	};

	const getPriceForCurrency = (product, currency) => {
		if (!product || !currency) return 0;
		const priceKey = `price_${currency}`;
		const currencyPrice = product[priceKey];
		const finalPrice = product.purchasable ? currencyPrice : 0;
		if (!finalPrice && finalPrice !== 0) return 0;
		const cleanPrice = String(finalPrice).replace(/[^\d.-]/g, '');
		const numericPrice = parseFloat(cleanPrice);
		return isNaN(numericPrice) ? 0 : numericPrice;
	};

	const sortProductsByOption = (items, value) => {
		if (!value) return items;
		const sortedProducts = [...items];
		switch (value) {
			case t('GENERAL.NAME_ASC'):
				sortedProducts.sort((a, b) =>
					(a.product_name || "").trim().localeCompare((b.product_name || "").trim())
				);
				break;
			case t('GENERAL.NAME_DESC'):
				sortedProducts.sort((a, b) =>
					(b.product_name || "").trim().localeCompare((a.product_name || "").trim())
				);
				break;
			case t('GENERAL.NUMBER_ASC'):
				sortedProducts.sort((a, b) =>
					(a.product_number || "").trim().localeCompare((b.product_number || "").trim())
				);
				break;
			case t('GENERAL.NUMBER_DESC'):
				sortedProducts.sort((a, b) =>
					(b.product_number || "").trim().localeCompare((a.product_number || "").trim())
				);
				break;
			case t('GENERAL.PRICE_ASC'):
				sortedProducts.sort((a, b) => {
					const priceA = getPriceForCurrency(a, selectedSiteCurrency);
					const priceB = getPriceForCurrency(b, selectedSiteCurrency);
					console.log(`Comparing: ${a.product_name} (${selectedSiteCurrency}${priceA}) vs ${b.product_name} (${selectedSiteCurrency}${priceB})`);
					return priceA - priceB;
				});
				break;
			case t('GENERAL.PRICE_DESC'):
				sortedProducts.sort((a, b) => {
					const priceA = getPriceForCurrency(a, selectedSiteCurrency);
					const priceB = getPriceForCurrency(b, selectedSiteCurrency);
					return priceB - priceA;
				});
				break;
			default:
				return items;
		}
		return sortedProducts;
	};

	const handleSortChange = (value) => {
		setSortOption(value);
		if (!value) return;
		setProducts((prevProducts) => sortProductsByOption(prevProducts, value));
	};

	const addAllByCategory = async (categoryLabel) => {
		try {
			const candidates = products.filter(p => p.ProductType === categoryLabel);
			if (candidates.length === 0) {
				Swal.fire({ icon: 'info', title: t('CART.NO_PARTS_FOUND'), text: `${t('CART.NO_PARTS_UNDER_CATEGORY').replace('{categoryLabel}', categoryLabel)}` });
				return;
			}
			const purchasable = candidates;
			const skipped = 0;
			if (purchasable.length === 0) {
				Swal.fire({ icon: 'info', title: t('CART.NO_PURCHASABLE_PARTS'), text: `${t('CART.ALL_CATEGORY_PARTS_ARE_NON_PURCHASABLE').replace('{categoryLabel}', categoryLabel)}` });
				return;
			}
			const items = purchasable.map(p => ({
				ProductID: p.product_id,
				Quantity: 1,
				Site: selectedSite,
				site_id: selectedSiteData?.site_id,
				cus_id: selectedSiteData?.cus_id,
				machine_id: p.machine_id || machineID || null,
				site_line_id: p.site_line_id || selectedSiteLineId || null,
				line_name: p.line_name || line_name || selectedLineName || "",
				line_number: p.line_number || line_number || selectedLineNumber || "",
			}));
			await addMultipleToCart(items);
			Swal.fire({ icon: 'success', title: t('CART.ADDED_TO_CART_1'), text: `${t('CART.ADDED_X_CATEGORY_PARTS_SKIPPED_Y').replace('{count}', purchasable.length).replace('{categoryLabel}', categoryLabel).replace('{skipped}', skipped)}` });
		} catch (e) {
			Swal.fire({ icon: 'error', title: t('GENERAL.FAILED'), text: t('CART.COULD_NOT_ADD_PARTS_TO_CART') });
		}
	};

	useEffect(() => {
		const fetchMachineDetails = async () => {
			if (!machineID) return;
			// Wait for site context to actually finish loading before checking site ownership --
			// firing this while selectedSiteData is still empty skips the site filter entirely
			// and can show a different customer's machine as if it belonged here.
			if (siteContextLoading) return;
			try {
					const reqBody = {
						machines: [machineID],
						site_line_id: selectedSiteLineId,
						site_number: selectedSite,
						site_id: selectedSiteData?.site_id,
						cus_id: selectedSiteData?.cus_id,
					};
				const response = await getMachinesByIds(reqBody);
				if (response.status && Array.isArray(response.data) && response.data.length > 0) {
					const machineData = response.data[0] || {};
					const resolvedName =
						machineData.sm_machine_name ||
						machineData.machine_name ||
						machineData.description ||
						"";
						setmachine_name(resolvedName || "");
							setmachine_number(machineData.machine_number || machineID || "");
					setLineName(machineData.line_name || "");
					setLineNumber(machineData.line_number || "");
						setMachineSerialForDocs(
							machineData.machine_product_name ||
							machineData.machine_serial_number ||
							machineData.machine_number ||
							machineID ||
							""
						);
						// Remember this as the machine the user was last looking at, so a part link
						// clicked from a 2D drawing opened here can prefer this machine when the same
						// part also exists on other machines at this site.
						if (selectedSiteData?.site_id && selectedSiteData?.cus_id) {
							try {
								localStorage.setItem("last_clicked_machine_id", String(machineID));
							} catch {
								// Non-critical if storage isn't available.
							}
						}
						const fallbackModelData = machineData?.model_data || machineData?.modelData || {};
						if (hasRenderableModelData(fallbackModelData)) {
							setModelData((prev) =>
								hasRenderableModelData(prev) ? prev : fallbackModelData
							);
							const { has2D, has3D, multiple } = getModelCapabilities(fallbackModelData);
							setHas2DModel((prev) => prev || has2D);
							setHas3DModel((prev) => prev || has3D);
							setHasMultipleModels((prev) => prev || multiple);
						}
					} else {
						setError("Failed to fetch data: No success flag.");
					}
				} catch (err) {
					setError("Failed to fetch data: " + err.message);
				}
		};
		fetchMachineDetails();
	}, [machineID, selectedSite, selectedSiteData?.site_id, selectedSiteData?.cus_id, selectedSiteLineId, siteContextLoading]);

	useEffect(() => {
		const fetchModelData = async () => {
			if (!selectedSite || !machine_number) return;
			if (hasRenderableModelData(modelData)) return;
			try {
				const resp = await getMachinesWithModels({
					site_id: selectedSiteData?.site_id,
					cus_id: selectedSiteData?.cus_id,
					site_line_id: selectedSiteLineId,
					page: 1,
					limit: 1,
					searchTerm: machine_number,
				});
				const rows = resp?.data?.data || [];
				const normalizedMachineNumber = String(machine_number).trim().toLowerCase();
				const row =
					rows.find((item) => {
						const rowMachineNumber = String(item?.machine_number || "").trim().toLowerCase();
						const rowMachineId = String(item?.machine_id || item?.machineId || "").trim();
						return (
							(normalizedMachineNumber && rowMachineNumber === normalizedMachineNumber) ||
							(rowMachineId && rowMachineId === String(machineID))
						);
					}) || rows[0];
				const md = row?.model_data || row?.modelData || {};
				if (!hasRenderableModelData(md)) {
					return;
				}
				setModelData(md);
				const { has2D, has3D, multiple } = getModelCapabilities(md);
				setHas2DModel(has2D);
				setHas3DModel(has3D);
				setHasMultipleModels(multiple);
			} catch (e) {
				console.warn("Failed to fetch machine model data from /machines/with-models:", e);
			}
		};
		fetchModelData();
	}, [machineID, machine_number, selectedSite, selectedSiteData?.cus_id, selectedSiteLineId, modelData]);

	const handle3DButtonClick = async () => {
		setM3DButtonLoading(true);
		try {
			if (hasMultipleModels) {
				setModelsModalOpen(true);
				setM3DButtonLoading(false);
				return;
			}
			if (modelData?.["3dfile"]) {
				window.open(`${SERVER_BASE_URL}${modelData["3dfile"]}`);
				trackModelOpened("3D", {
					machine_id: machineID,
					machine_name,
					machine_number,
					model_file: extractFilename(modelData["3dfile"]),
					model_source: MODELSOURCE.PRODUCT_PAGE,
				});
				setM3DButtonLoading(false);
				return;
			}
			const response = await processModelFile({
				filename: machine_number,
				machinename: machine_name,
				currency: selectedSiteCurrency.toUpperCase(),
				loggedinUser: user.user_id,
				ecommachineid: machineID,
				baseColor: theme.palette.primary.main,
				selectedSite,
				machinenumber: machine_number,
			});
			if (response.status === 200) {
				const backendUrl = SERVER_BASE_URL || "";
				const fullUrl = backendUrl + response.data.url;
				setM3DButtonLoading(false);
				window.open(fullUrl);
				trackModelOpened("3D", {
					machine_id: machineID,
					machine_name,
					machine_number,
					model_file: machine_number,
					model_source: MODELSOURCE.PRODUCT_PAGE,
				});
			}
		} catch (err) {
			console.error("Error fetching the model:", err);
		}
	};

	const handle2DButtonClick = async () => {
		setM3DButtonLoading(true);
		try {
			if (hasMultipleModels) {
				setModelsModalOpen(true);
				setM3DButtonLoading(false);
				return;
			}
			if (modelData?.["2dfile"]) {
				window.open(`${SERVER_BASE_URL}${modelData["2dfile"]}`);
				trackModelOpened("2D", {
					machine_id: machineID,
					machine_name,
					machine_number,
					model_file: extractFilename(modelData["2dfile"]),
					model_format: "pdf",
					model_source: MODELSOURCE.PRODUCT_PAGE,
				});
				setM3DButtonLoading(false);
				return;
			}
			const response = await process2DModelFile({
				filename: machine_number,
				machinenumber: machine_number,
			});
			if (response.status === 200) {
				const backendUrl = SERVER_BASE_URL || "";
				const fullUrl = backendUrl + response.data.url;
				setM3DButtonLoading(false);
				window.open(fullUrl);
				trackModelOpened("2D", {
					machine_id: machineID,
					machine_name,
					machine_number,
					model_file: machine_number,
					model_format: "pdf",
					model_source: MODELSOURCE.PRODUCT_PAGE,
				});
			}
		} catch (err) {
			console.error("Error fetching the 2D model:", err);
		} finally {
			setM3DButtonLoading(false);
		}
	};

	useEffect(() => {
		const fetchAllData = async () => {
			if (!machineID || !selectedSite || selectedSite === "") {
				return;
			}
			setLoading(true);
			setBomLoading(true);
			setBomSelectionReady(false);
			setError(null);
			try {
				const productsReqBody = {
					machine_id: machineID,
					site_number: selectedSite,
					site_line_id: selectedSiteLineId,
				};
				const productsResponse = await getProductsByMachineId(productsReqBody);
				if (productsResponse.status) {
					const {
						products: machineProducts,
						bom,
						bomTree,
					} = normalizeMachineProductsResponse(productsResponse);
					const spareLevelLookup = buildSpareLevelLookup(machineProducts, bom, bomTree);
					const productsWithType = machineProducts.map((product) => {
						const productType = getRsplLabelForCode(
							product.product_rspl_code,
							rsplMapping,
							t('UI_TEXT.OTHER')
						);
						const spareLevel =
							getSpareLevelFromRecord(product) ||
							spareLevelLookup.byProductId.get(String(product?.product_id ?? "").trim()) ||
							spareLevelLookup.byProductNumber.get(String(product?.product_number ?? "").trim()) ||
							"";
						return {
							...product,
							site_line_id: product.site_line_id || selectedSiteLineId,
							ProductType: productType,
							SpareLevel: spareLevel,
						};
					});
					const defaultSortLabel = getSortLabelFromKey(optionKeys?.productsDefaultSortKey);
					setSortOption(defaultSortLabel);
					let finalSortedProducts = sortProductsByOption(productsWithType, defaultSortLabel);

					try {
						const savedResponse = await getSavedForLaterBySiteId({
							site_id: selectedSiteData?.site_id,
							cus_id: selectedSiteData?.cus_id,
							customer_site_number: selectedSite
						});
						if (savedResponse?.status && Array.isArray(savedResponse.data)) {
							const savedProductIds = new Set(
								savedResponse.data.map((item) => Number(item.product_id))
							);
							finalSortedProducts = finalSortedProducts.map((p) => ({
								...p,
								savedForLater: savedProductIds.has(Number(p.product_id))
							}));
						}
					} catch (savedErr) {
						console.error("Error fetching saved for later products:", savedErr);
					}

					setBomTreeData(bomTree || []);
					setBomRows(Array.isArray(bom) ? bom : []);
					setSelectedBomNodeId(null);
					setProducts(finalSortedProducts);
					setTotalPages(Math.ceil(productsWithType.length / pageSize));
					setDefaultFilters(productsWithType);
					setLoading(false);
				} else {
					setBomTreeData([]);
					setBomRows([]);
					setError("Failed to fetch products: No success flag.");
				}

				if (topFetchTimer.current) clearTimeout(topFetchTimer.current);
				topFetchTimer.current = setTimeout(async () => {
					try {
						setTopProductsLoading(true);
						const topProductsReqBody = {
							machine_id: machineID,
							site_number: selectedSite,
							site_line_id: selectedSiteLineId,
							cus_id: selectedSiteData?.cus_id,
						};
						const topProductsResponse = await getTopPurchasedProduct(topProductsReqBody);
						if (topProductsResponse.status) {
							setTopProductsLoading(false);
							setTopProducts(topProductsResponse.data);
						} else {
							setTopProductsLoading(false);
							setTopProducts([]);
						}
					} catch (topProductsError) {
						setTopProductsLoading(false);
						setTopProducts([]);
						console.warn("Failed to fetch top products:", topProductsError);
					}
				}, 400);
			} catch (err) {
				setError("Failed to fetch products: " + err.message);
				setBomTreeData([]);
				setBomRows([]);
			} finally {
				setBomLoading(false);
				setLoading(false);
			}
		};
		fetchAllData();
		return () => {
			if (topFetchTimer.current) clearTimeout(topFetchTimer.current);
		};
	}, [
		getSortLabelFromKey,
		machineID,
		optionKeys?.productsDefaultFilterTypes,
		optionKeys?.productsDefaultSortKey,
		pageSize,
		selectedSite,
		selectedSiteLineId,
	]);

	const setDefaultFilters = (productsWithType = []) => {
		setSelectedSpareLevels(null);
		const allDetectedTypes = Array.from(
			new Set(
				(productsWithType || [])
					.map((product) => product.ProductType)
					.filter(Boolean)
			)
		);
		const configuredTypes = parseJsonArray(optionKeys?.productsDefaultFilterTypes, ["__ALL__"])
			.map((value) => String(value || "").trim())
			.filter(Boolean);
		if (configuredTypes.length === 0 || configuredTypes.includes("__ALL__")) {
			setSelectedFilters(null);
			return;
		}
		const selectedConfiguredTypes = allDetectedTypes.filter((type) =>
			configuredTypes.includes(type)
		);
		setSelectedFilters(
			selectedConfiguredTypes.length === allDetectedTypes.length ? null : selectedConfiguredTypes
		);
	};

	const allPartTypesSelected = selectedFilters === null;
	const effectiveSelectedFilters = allPartTypesSelected ? availableCategoryArray : selectedFilters;
	const allSpareLevelsSelected = selectedSpareLevels === null;
	const effectiveSelectedSpareLevels = allSpareLevelsSelected ? availableSpareLevelArray : selectedSpareLevels;

	const categoryFilteredProducts = useMemo(() => {
		if (allPartTypesSelected) return products;
		if (availableCategoryArray.length > 0 && effectiveSelectedFilters.length === 0) return [];
		return products.filter((item) => effectiveSelectedFilters.includes(item.ProductType));
	}, [allPartTypesSelected, products, effectiveSelectedFilters, availableCategoryArray]);

	const spareLevelFilteredProducts = useMemo(() => {
		if (allSpareLevelsSelected) return categoryFilteredProducts;
		if (availableSpareLevelArray.length > 0 && effectiveSelectedSpareLevels.length === 0) return [];
		return categoryFilteredProducts.filter((item) => effectiveSelectedSpareLevels.includes(item.SpareLevel));
	}, [allSpareLevelsSelected, categoryFilteredProducts, effectiveSelectedSpareLevels, availableSpareLevelArray]);

	const allCategoriesSelected =
		availableCategoryArray.length > 0 &&
		allPartTypesSelected;
	const allSpareFiltersSelected =
		availableSpareLevelArray.length > 0 &&
		allSpareLevelsSelected;

	const textFilteredProducts = useMemo(() => {
		if (!normalizedSearchTerm) return spareLevelFilteredProducts;
		return spareLevelFilteredProducts.filter(
			(item) =>
				item.product_name?.toLowerCase().includes(normalizedSearchTerm) ||
				item.product_number?.toLowerCase().includes(normalizedSearchTerm)
		);
	}, [spareLevelFilteredProducts, normalizedSearchTerm]);

	const filteredProducts = useMemo(() => {
		if (!hasBomData) return textFilteredProducts;
		if (checkedProductIdSet.size === 0) return [];
		return textFilteredProducts.filter((item) =>
			checkedProductIdSet.has(item.product_id)
		);
	}, [textFilteredProducts, checkedProductIdSet, hasBomData]);

	const filtersActive =
		(
			!allPartTypesSelected &&
			effectiveSelectedFilters.length > 0 &&
			effectiveSelectedFilters.length < availableCategoryArray.length
		) ||
		(
			!allSpareLevelsSelected &&
			effectiveSelectedSpareLevels.length > 0 &&
			effectiveSelectedSpareLevels.length < availableSpareLevelArray.length
		);

	const highlightedProductIds = useMemo(() => {
		if (!filtersActive) return new Set();
		return new Set(filteredProducts.map((item) => item.product_id));
	}, [filtersActive, filteredProducts]);

	const shouldShowBomSelectionAlert =
		hasBomData && bomSelectionReady && !bomLoading && checkedBomNodeIds.length === 0;

	const commonBomViewerProps = {
		title: t("UI_TEXT.BOM_TREE_TITLE"),
		helperText: t("UI_TEXT.BOM_TREE_HELPER"),
		clearSelectionLabel: t("UI_TEXT.CLEAR_BOM_SELECTION"),
		noDataLabel: t("UI_TEXT.NO_BOM_DATA"),
		bomSearchPlaceholder: t("UI_TEXT.BOM_TREE_SEARCH_PLACEHOLDER"),
		machineName: normalizedMachineName || t("UI_TEXT.MACHINE"),
		machineNumber: normalizedMachineNumber || machineID || "",
		machineNodeId,
		machineProductIds,
		machineNodeIds,
		treeData: bomTreeData,
		selectedNodeId: selectedBomNodeId,
		expandedNodes,
		searchTerm: productSearchTerm,
		checkedProductIds: checkedBomProductIds,
		checkedNodeIds: checkedBomNodeIds,
		onNodeSelect: handleTreeSelection,
		onSelectionChange: handleBomSelectionChange,
		onClearSelection: handleClearTreeSelection,
		onToggle: setExpandedNodes,
		filtersActive,
		highlightedProductIds,
		expandAllLabel: t("UI_TEXT.EXPAND_ALL"),
		collapseAllLabel: t("UI_TEXT.COLLAPSE_ALL"),
		modalButtonLabel: t("UI_TEXT.VIEW_BOM_IN_MODAL"),
		isLoading: bomLoading,
		onProductClick: handleBomProductClick,
		modelAvailability: bomModelAvailability,
		onModelIconClick: handleBomModelIconClick,
		machineHasDocumentation: shouldShowBomDocIcon,
		onMachineDocumentationClick: handleDocumentationNavigate,
		onExportBom: showBomExportButton ? handleExportBom : undefined,
	};

	useEffect(() => {
		if (!hasBomData || syncSourceRef.current !== "filters") return;
		const activeFilterGroup = activeFilterGroupRef.current;
		if (
			(activeFilterGroup === "partType" && allPartTypesSelected) ||
			(activeFilterGroup === "spareLevel" && allSpareLevelsSelected) ||
			(!activeFilterGroup && allPartTypesSelected && allSpareLevelsSelected)
		) { 
			setCheckedBomNodeIds(machineNodeIds);
			syncSourceRef.current = null;
			activeFilterGroupRef.current = null;
			return;
		}
		const selectedCategorySet = new Set(effectiveSelectedFilters);
		const selectedSpareLevelSet = new Set(effectiveSelectedSpareLevels);
		const nextCheckedSet = new Set();
		    flatNodes.forEach((node) => {
			const productId = node?.productId;
			const nodeId = node?.nodeId;
			if (!productId || !nodeId) return;
			const type = productTypeByProductId.get(productId);
			const spareLevel = spareLevelByProductId.get(productId);
			const matchesPartType =
				activeFilterGroup === "partType" &&
				!allPartTypesSelected &&
				selectedCategorySet.has(type);
			const matchesSpareLevel =
				activeFilterGroup === "spareLevel" &&
				!allSpareLevelsSelected &&
				selectedSpareLevelSet.has(spareLevel);
			if (matchesPartType || matchesSpareLevel) {
				nextCheckedSet.add(nodeId);
			}
		});
		const nextChecked = machineNodeIds.filter((nodeId) =>
			nextCheckedSet.has(nodeId)
		);
		setCheckedBomNodeIds(nextChecked);
		syncSourceRef.current = null;
		activeFilterGroupRef.current = null;
	}, [
		allPartTypesSelected,
		allSpareLevelsSelected,
		effectiveSelectedFilters,
		effectiveSelectedSpareLevels,
		flatNodes,
		hasBomData,
		machineNodeIds,
		machineProductIds,
		productTypeByProductId,
		spareLevelByProductId,
	]);

	// useEffect(() => {
	// 	if (!hasBomData || syncSourceRef.current !== "bom") return;
	// 	syncSourceRef.current = null;

	// 	const machineProductIdSet = new Set(machineProductIds);
	// 	const checkedSet = new Set(
	// 		(checkedBomProductIds || []).filter((productId) => machineProductIdSet.has(productId))
	// 	);

	// 	const fullySelectedTypes = availableCategoryArray.filter((type) => {
	// 		const idsForType = machineProductIds.filter(
	// 			(id) => productTypeByProductId.get(id) === type
	// 		);
	// 		return idsForType.length > 0 && idsForType.every((id) => checkedSet.has(id));
	// 	});
	// 	const nextPartTypeSelection =
	// 		fullySelectedTypes.length === availableCategoryArray.length
	// 			? null
	// 			: fullySelectedTypes;

	// 	const fullySelectedLevels = availableSpareLevelArray.filter((level) => {
	// 		const idsForLevel = machineProductIds.filter(
	// 			(id) => spareLevelByProductId.get(id) === level
	// 		);
	// 		return idsForLevel.length > 0 && idsForLevel.every((id) => checkedSet.has(id));
	// 	});
	// 	const nextSpareLevelSelection =
	// 		fullySelectedLevels.length === availableSpareLevelArray.length
	// 			? null
	// 			: fullySelectedLevels;

	// 	setSelectedFilters((prev) =>
	// 		areSelectionArraysEqual(prev, nextPartTypeSelection) ? prev : nextPartTypeSelection
	// 	);
	// 	setSelectedSpareLevels((prev) =>
	// 		areSelectionArraysEqual(prev, nextSpareLevelSelection) ? prev : nextSpareLevelSelection
	// 	);
	// }, [
	// 	availableCategoryArray,
	// 	availableSpareLevelArray,
	// 	checkedBomProductIds,
	// 	hasBomData,
	// 	machineProductIds,
	// 	productTypeByProductId,
	// 	spareLevelByProductId,
	// ]);

	useEffect(() => {
		if (!hasBomData || syncSourceRef.current !== "filters") return;
		if (allPartTypesSelected) {
			setCheckedBomProductIds((prev) => {
				if (prev.length === machineProductIds.length) {
					const prevSet = new Set(prev);
					const isSame = machineProductIds.every((id) => prevSet.has(id));
					if (isSame) return prev;
				}
				return machineProductIds;
			});
			syncSourceRef.current = null;
			return;
		}
		const selectedCategorySet = new Set(effectiveSelectedFilters);
		const nextCheckedSet = new Set();
		selectedCategorySet.forEach((category) => {
			const productIds = productIdsByCategory.get(category) || [];
			productIds.forEach((productId) => nextCheckedSet.add(productId));
		});
		const nextChecked = machineProductIds.filter((productId) =>
			nextCheckedSet.has(productId)
		);
		setCheckedBomProductIds((prev) => {
			if (prev.length === nextChecked.length) {
				const prevSet = new Set(prev);
				const isSame = nextChecked.every((id) => prevSet.has(id));
				if (isSame) return prev;
			}
			return nextChecked;
		});
		syncSourceRef.current = null;
	}, [allPartTypesSelected, effectiveSelectedFilters, hasBomData, productIdsByCategory, machineProductIds]);

	useEffect(() => {
		if (!hasBomData || syncSourceRef.current !== "bom") return;
		const selectedTypeSet = new Set();
		checkedBomProductIds.forEach((productId) => {
			const type = productTypeByProductId.get(productId);
			if (type) {
				selectedTypeSet.add(type);
			}
		});
		const nextSelectedFilters = availableCategoryArray.filter((category) =>
			selectedTypeSet.has(category)
		);
		setSelectedFilters((prev) => {
			const normalizedPrev = prev === null ? availableCategoryArray : prev;
			if (normalizedPrev.length === nextSelectedFilters.length) {
				const prevSet = new Set(normalizedPrev);
				const isSame = nextSelectedFilters.every((value) => prevSet.has(value));
				if (isSame) return prev;
			}
			return nextSelectedFilters.length === availableCategoryArray.length ? null : nextSelectedFilters;
		});
		syncSourceRef.current = null;
	}, [
		checkedBomProductIds,
		hasBomData,
		productTypeByProductId,
		availableCategoryArray,
	]);

	useEffect(() => {
		const pages = Math.ceil(filteredProducts.length / pageSize);
		setTotalPages(pages);
		setCurrentPage((prevPage) => {
			const maxPage = Math.max(pages, 1);
			if (prevPage > maxPage) return maxPage;
			return prevPage < 1 ? 1 : prevPage;
		});
	}, [filteredProducts, pageSize]);

	useEffect(() => {
		setCurrentPage(1);
	}, [normalizedSearchTerm, selectedFilters, selectedSpareLevels, hasBomData, sortOption]);

	useEffect(() => {
		treeItemRefs.current = {};
	}, [bomTreeData]);

	useEffect(() => {
		if (bomLoading) {
			setBomSelectionReady(false);
		}
		if (flatNodes.length === 0) {
			setCheckedBomProductIds([]);
			setCheckedBomNodeIds([]);
			if (!bomLoading) {
				setBomSelectionReady(true);
			}
			return;
		}

		// ── FIX: Don't overwrite user's manual BOM selection ──
		if (syncSourceRef.current === "bom") {
			setBomSelectionReady(true);
			return;
		}

		const seen = new Set();
		const allIds = [];
		const allNodeIds = [];
		flatNodes.forEach((node) => {
			if (node.nodeId && node.productId) {
				allNodeIds.push(node.nodeId);
			}
			if (node.productId && !seen.has(node.productId)) {
				seen.add(node.productId);
				allIds.push(node.productId);
			}
		});
		setCheckedBomProductIds((prev) => {
			if (prev.length === allIds.length) {
				const prevSet = new Set(prev);
				const isSame = allIds.every((id) => prevSet.has(id));
				if (isSame) return prev;
			}
			return allIds;
		});
		setCheckedBomNodeIds((prev) => {
			if (prev.length === allNodeIds.length) {
				const prevSet = new Set(prev);
				const isSame = allNodeIds.every((id) => prevSet.has(id));
				if (isSame) return prev;
			}
			return allNodeIds;
		});
		setBomSelectionReady(true);
	}, [flatNodes]);

	useEffect(() => {
		if (!hasBomData) setSelectedBomNodeId(null);
	}, [hasBomData]);

	useEffect(() => {
		setExpandedNodes([machineNodeId]);
		setSelectedBomNodeId(null);
	}, [machineNodeId]);

	useEffect(() => {
		if (bomModalOpen) {
			const timer = setTimeout(() => setBomModalOpening(false), 300);
			return () => clearTimeout(timer);
		}
		setBomModalOpening(false);
	}, [bomModalOpen]);

	useEffect(() => {
		if (!normalizedSearchTerm || searchMatchIds.length === 0) return;
		const targetId = searchMatchIds[0];
		const ancestors = collectAncestorIds(targetId, nodeMap);
		if (ancestors.length > 0 || targetId) {
			setExpandedNodes((prev) => {
				const unique = new Set(prev);
				ancestors.forEach((id) => unique.add(id));
				unique.add(machineNodeId);
				return Array.from(unique);
			});
		}
		const scrollToNode = treeItemRefs.current?.__scrollToNode;
		if (typeof scrollToNode === "function") {
			scrollToNode(targetId);
			return;
		}
		const targetNode = treeItemRefs.current[targetId];
		if (targetNode && targetNode.scrollIntoView) {
			targetNode.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [normalizedSearchTerm, searchMatchIds, nodeMap, machineNodeId]);

	// ── CHANGE 3: Standard page-based slicing (was cumulative infinite scroll) ──
	const currentProducts = useMemo(
		() => filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize),
		[filteredProducts, currentPage, pageSize]
	);

	// ── CHANGE 4 & 5: REMOVED hasMoreProducts, IntersectionObserver useEffect,
	//                  and loadMorePendingRef reset useEffect ──

	const needsBomSelectionReady = hasBomData;
	if (
		loading ||
		bomLoading ||
		(needsBomSelectionReady && !bomSelectionReady) ||
		!selectedSite ||
		selectedSite === ""
	) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
				<CircularProgress />
				<Typography sx={{ ml: 2 }}>
					{!selectedSite || selectedSite === ""
						? t('UI_TEXT.LOADING_SITE_INFO')
						: t('UI_TEXT.LOADING_PARTS')}
				</Typography>
			</Box>
		);
	}

	if (error) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Alert severity="error" className="error-alert">{error}</Alert>
			</Box>
		);
	}

	if (!loading && selectedSite && selectedSite !== "" && products.length === 0) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Alert severity="info" className="error-alert">
					{t("ALERT_MESSAGES.NO_PRODUCTS_FOUND_MACHINE")}
				</Alert>
			</Box>
		);
	}

	if (!machineID) return <PageNotFound />;

	return (
		<>
			<div className="products-page">
				<PageTitle title={pageTitleText} subheader={line_name || ""} />
				<div className="products-content">
					<div className="filters">
						<div className="filter-content" data-tour="product-search-filter-bom">
							{/* Search and sort controls */}
							{!showBomTreeOnMainCanvas && (
								<>
									<Box sx={{ display: "flex", columnGap: 1, alignItems: "center" }}>
										<Box sx={{ flex: "0 0 80%" }}>
											<ContextFilterAndSort
												value={productSearchTerm}
												onSearchChange={handleProductSearchChange}
												placeholder={t("PLACEHOLDER.SEARCH_PART")}
												options={sortOptions}
												selectedOption={sortOption}
												onSortChange={handleSortChange}
												showSearch={true}
												showSort={true}
											/>
										</Box>
										<Box sx={{ flex: "0 0 20%" }}>
											<TextField
												select
												size="small"
												label={t('GENERAL.PER_PAGE')}
												value={pageSize}
												onChange={handlePageSizeChange}
												fullWidth
												SelectProps={{ MenuProps: { disableScrollLock: true } }}
											>
												{pageSizeOptions.map((size) => (
													<MenuItem key={size} value={size}>{size}</MenuItem>
												))}
											</TextField>
										</Box>
									</Box>
									<Typography variant="body2" sx={{ color: "text.secondary", fontSize: "11px", mt: 0.5 }}>
										{t("UI_TEXT.APPLIED_SORTING", { sort: sortOption || t("MENU_ITEMS.NONE") })}
									</Typography>
									<Box sx={{ display: "flex", flexDirection: "column", rowGap: 1, width: "100%", alignItems: "stretch", mt: 2 }}>
										{bulkAddCategories.map(({ category, purchasableCount }) => (
											<Button
												key={category}
												variant="outlined"
												startIcon={<ShoppingCartOutlinedIcon />}
												onClick={() => addAllByCategory(category)}
												disabled={purchasableCount === 0}
											>
												{getBulkButtonLabel(category, purchasableCount)}
											</Button>
										))}
									</Box>
								</>
							)}

							{/* Product category filters accordion */}
							<Accordion defaultExpanded sx={{ marginTop: showBomTreeOnMainCanvas ? 0 : "10px", borderRadius: "5px", boxShadow: "none", border: "1px solid #ddd" }}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
									<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>
										{t("UI_TEXT.PART_TYPE_FILTERS")}
									</Typography>
								</AccordionSummary>
								<AccordionDetails className="filters-list">
									<FormControlLabel
										control={<Checkbox checked={allCategoriesSelected} onChange={handleAllChange} value={t('MENU_ITEMS.ALL')} />}
										label={`${t('MENU_ITEMS.ALL')} (${products.length})`}
									/>
									{availableCategoryArray.map((productType) => {
										const count = products.filter((product) => product.ProductType === productType).length;
										let label = productType;
										return (
											products.some((product) => product.ProductType === productType) && (
												<FormControlLabel
													key={productType}
													control={
														<Checkbox
															checked={effectiveSelectedFilters.includes(productType)}
															onChange={handleFilterChange}
															value={productType}
														/>
													}
													label={`${label} (${count})`}
												/>
											)
										);
									})}
								</AccordionDetails>
							</Accordion>

							<Accordion defaultExpanded sx={{ marginTop: "10px", borderRadius: "5px", boxShadow: "none", border: "1px solid #ddd" }}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel-spare-level-content" id="panel-spare-level-header">
									<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>
										{t("UI_TEXT.SPARE_LEVEL_FILTERS", { defaultValue: "Spare Level Filters" })}
									</Typography>
								</AccordionSummary>
								<AccordionDetails className="filters-list">
									<FormControlLabel
										control={<Checkbox checked={allSpareFiltersSelected} onChange={handleAllSpareLevelsChange} value={t('MENU_ITEMS.ALL')} />}
										label={`${t('MENU_ITEMS.ALL')} (${products.length})`}
									/>
									{availableSpareLevelArray.map((spareLevel) => {
										const count = products.filter((product) => product.SpareLevel === spareLevel).length;
										const spareLevelTooltip = SPARE_LEVEL_TOOLTIPS[spareLevel];
										const displayLabel = spareLevelTooltip
											? `${spareLevelTooltip} (${count})`
											: `${t("UI_TEXT.SPARE_LEVEL", { defaultValue: "Spare Level" })} ${spareLevel} (${count})`;
										const hoverTooltip = `${t("UI_TEXT.SPARE_LEVEL", { defaultValue: "Spare Level" })} ${spareLevel}`;
										return (
											<FormControlLabel
												key={spareLevel}
												control={
													<Checkbox
														checked={effectiveSelectedSpareLevels.includes(spareLevel)}
														onChange={handleSpareLevelFilterChange}
														value={spareLevel}
													/>
												}
												label={
													<Tooltip title={hoverTooltip} arrow placement="right" componentsProps={{ tooltip: { sx: { fontSize: "13px", padding: "10px 12px", maxWidth: "220px", lineHeight: 1.3 } } }}>
														<span style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>
															{displayLabel}
														</span>
													</Tooltip>
												}
											/>
										);
									})}
								</AccordionDetails>
							</Accordion>

							{!showBomTreeOnMainCanvas && (
								<BomViewer
									{...commonBomViewerProps}
									treeItemRefs={treeItemRefs}
									maxDepth={10}
									onOpenModal={handleOpenBomModal}
									modalOpening={bomModalOpening}
								/>
							)}

							{/* Action buttons */}
							<Box sx={{ display: "flex", flexDirection: "row",
									flexWrap: "wrap",
									alignItems: "center",
									justifyContent: "flex-start",
									columnGap: 1.25,
									rowGap: 1,
									marginTop: 2,
									width: "100%",
									"& .MuiButton-root": {
										flex: "1 1 0",
										minWidth: 0,
										px: 1,
										fontSize: "0.875rem",
										lineHeight: 1.2,
										whiteSpace: "nowrap",
									},
									"& .MuiButton-startIcon": {
										mr: 0.5,
										ml: 0,
									},
									"& .MuiButton-startIcon svg": {
										fontSize: 20,
									},
									...(has3DModel && has2DModel && shouldShowBomDocIcon
										? { "& .documentation-action-button": { flex: "0 0 100%" } }
										: {}),
								}}
							>
								{has3DModel && (
									<Button variant="contained" startIcon={<ViewInArIcon />} onClick={() => handle3DButtonClick()}>
										{m3DButtonLoading ? (
											<>{t("UI_TEXT.LOADING")}<CircularProgress color="white" size={20} sx={{ marginLeft: "5px" }} /></>
										) : (
											t("UI_TEXT.MODEL_3D")
										)}
									</Button>
								)}
								{has2DModel && (
									<Button variant="contained" startIcon={<DescriptionIcon />} onClick={() => handle2DButtonClick()}>
										{t('UI_TEXT.MODEL_2D')}
									</Button>
								)}
								{shouldShowBomDocIcon && (
									<Button variant="contained" startIcon={<DescriptionIcon />} onClick={handleDocumentationNavigate}
										className="documentation-action-button"
									>
										{t("BUTTON.DOCUMETATION")}
									</Button>
								)}
							</Box>

							{topProductsLoading ? (
								<Box sx={{ mt: 2 }}>
									<Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
									<Box>
										{[...Array(4)].map((_, index) => (
											<Box key={index} sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
												<Skeleton variant="rectangular" width={64} height={64} />
												<Box sx={{ flex: 1 }}>
													<Skeleton width="60%" height={20} />
													<Skeleton width="40%" height={18} />
													<Skeleton width="80%" height={18} />
												</Box>
											</Box>
										))}
									</Box>
								</Box>
							) : (topProducts.length > 0) ? (
								<Box sx={{ mt: 2 }}>
									<PageTitle subheader={`Best Sellers for ${machine_name}`} />
									<Box>
										{topProducts.slice(0, 4).map((product) => (
											<TopProductListItem key={product.product_id} product={product} machine_id={machineID} />
										))}
									</Box>
								</Box>
							) : null}
						</div>
					</div>

					<Divider flexItem orientation="vertical" sx={{ my: 0.5 }} />

					{/* Main products display area */}
					<div className="products">
						{showBomTreeOnMainCanvas && (
							<Tooltip title={`${t("BUTTON.ADD_TO_QUOTE", { defaultValue: "Add to Quote" })} (${selectedBomCount})`}>
								<span className="bom-add-to-quote-fab">
									<Badge
										badgeContent={selectedBomCount}
										color="error"
										invisible={selectedBomCount === 0}
										overlap="circular"
									>
										<Fab
											color="primary"
											aria-label={t("BUTTON.ADD_TO_QUOTE", { defaultValue: "Add to Quote" })}
											onClick={handleAddSelectedToQuote}
											disabled={!hasBomData || selectedBomCount === 0}
										>
											<AddShoppingCartIcon />
										</Fab>
									</Badge>
								</span>
							</Tooltip>
						)}
						{showBomTreeOnMainCanvas ? (
							<Box sx={{ width: "100%" }}>
								<BomViewer
									{...commonBomViewerProps}
									variant="modal"
									maxHeight={720}
									onNavigateToProduct={handleNavigateToProductDetails}
								/>
							</Box>
						) : shouldShowBomSelectionAlert ? (
							<Alert severity="info" sx={{ width: "100%" }}>
								{t("UI_TEXT.SELECT_PARTS_IN_BOM_TREE")}
							</Alert>
						) : (
							<>
								<div className="products-list">
									{currentProducts.map((product) => {
										const spareLevel = product.SpareLevel;
										const spareLevelLabel =
											spareLevel && spareLevel !== "0" && SPARE_LEVEL_TOOLTIPS[spareLevel]
												? SPARE_LEVEL_TOOLTIPS[spareLevel]
												: null;
										return (
											<div key={product.product_id}>
												<ProductCard
													product={product}
													machineID={machineID}
													showSavedForLaterDial={true}
													showAddToQuote={true}
													spareLevelLabel={spareLevelLabel}
													onSaveForLater={(productId, isSaved) => {
														setProducts((prev) => 
															prev.map(p => p.product_id === productId ? { ...p, savedForLater: isSaved } : p)
														);
													}}
												/>
											</div>
										);
									})}
								</div>

								{/* ── CHANGE 5: Standard MUI Pagination replaces infinite scroll spinner ── */}
								{totalPages > 0 && (
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											flexWrap: "wrap",
											gap: 2,
											mt: 2,
											pb: 2,
										}}
									>
										{/* Rows-per-page selector — mirrors ManageUsers/Datagrid style */}
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography variant="body2" color="text.secondary">
												{t('GENERAL.ROWS_PER_PAGE', { defaultValue: 'Rows per page:' })}
											</Typography>
											<TextField
												select
												size="small"
												value={pageSize}
												onChange={handlePageSizeChange}
												sx={{ minWidth: 72 }}
												SelectProps={{ MenuProps: { disableScrollLock: true } }}
											>
												{[5, 10, 25, 50].map((size) => (
													<MenuItem key={size} value={size}>{size}</MenuItem>
												))}
											</TextField>
										</Box>

										{/* Page range label — mirrors "1–5 of 8" in ManageUsers */}
										<Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, textAlign: "center" }}>
											{filteredProducts.length > 0
												? `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, filteredProducts.length)} of ${filteredProducts.length}`
												: "0 results"}
										</Typography>

										{/* MUI Pagination component */}
										<Pagination
											count={totalPages}
											page={currentPage}
											onChange={(_, value) => setCurrentPage(value)}
											color="primary"
											shape="rounded"
											showFirstButton
											showLastButton
										/>
									</Box>
								)}
							</>
						)}
					</div>
				</div>
			</div>

			{/* Multiple Models Modal */}
			<MultipleModelViewer
				show={modelsModalOpen}
				setShow={setModelsModalOpen}
				machine_number={machine_number}
				machine_name={machine_name}
				productNumbers={[]}
				machine_id={machineID}
				has2D={has2DModel}
				modelData={modelData}
			/>
			{!showBomTreeOnMainCanvas && (
				<ModalWrapper
					open={bomModalOpen}
					setOpen={setBomModalOpen}
					title={t("UI_TEXT.BOM_TREE_MODAL_TITLE")}
					subtitle={t("UI_TEXT.BOM_TREE_MODAL_SUBTITLE")}
					width="60vw"
					content={
						<BomViewer
							{...commonBomViewerProps}
							variant="modal"
							onNavigateToProduct={handleNavigateToProductDetails}
						/>
					}
				/>
			)}
			<BomProductQuickView
				open={bomProductQuickView.open}
				productId={bomProductQuickView.productId}
				machineId={machineID}
				machineName={normalizedMachineName || machine_name || ""}
				onClose={handleCloseBomProductQuickView}
			/>
		</>
	);
};

export default Products;
